import React, {
  useRef,
  useEffect,
  useState,
  useContext,
  Fragment,
} from "react";
import PropTypes from "prop-types";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
} from "@syncfusion/ej2-react-grids";
import Tooltip from "@mui/material/Tooltip";
import AddLinkIcon from "@mui/icons-material/AddLink";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CustomAlert from "./CustomAlert";
import Typography from "@mui/material/Typography";
import { CredsContext, HelpModeContext } from "./Contexts";
import Tools from "./JNSTools";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MenuItem from "@mui/material/MenuItem";
import DashboardPanelHeader from "./DashboardPanelHeader";

import DashboardStyledMenu from "./styled components/DashboardStyledMenu";
import "./styles/DashboardProjectLinks.css";

function DashboardProjectLinks(props) {
  const [linkArray, setLinkArray] = useState([]);
  const [displayPage, setDisplayPage] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const creds = useContext(CredsContext).creds;
  const helpMode = useContext(HelpModeContext).mode;
  const workspaceid = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  let newLink = useRef({
    id: "",
    name: "",
    url: "",
  });
  const grid = useRef();
  const selectedLink = useRef();
  const urlString = Tools.getInstanceUrl(creds);
  const authString = Tools.getXmlBodyAuthString(creds);

  useEffect(() => {
    if (props.wlTable.length > 0) {
      const initialLinkRecord = props.wlTable.find(
        (elem) => elem.recordid == props.projectObject.id && elem.external != 1,
      );
      if (initialLinkRecord) {
        workspaceid.current = initialLinkRecord.workspaceid;

        let tempArray = [];
        for (let i = 0; i < props.wlTable.length; i++) {
          if (
            props.wlTable[i].workspaceid == initialLinkRecord.workspaceid &&
            props.wlTable[i].external == 1
          ) {
            tempArray.push(
              props.wlTable[i].id +
                "|" +
                props.wlTable[i].description +
                "[" +
                props.wlTable[i].url.replace(/%2F/g, "/") +
                "]",
            );
          }
        }

        const finalArray = [];
        tempArray.forEach((elem) =>
          finalArray.push({
            id: elem.substring(0, elem.indexOf("|")),
            url: elem.substring(elem.indexOf("[") + 1, elem.length - 1),
            name: elem.substring(elem.indexOf("|") + 1, elem.indexOf("[")),
          }),
        );
        setLinkArray(finalArray);
      }
    }
  }, [props.wlTable]);

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleOpen = (event) => {
    handleDotsClose();
    selectedLink.current = linkArray[event.target.dataset.linkinfo];
    loadDialogDefaults();
    if (!workspaceid.current) {
      const tempWsId = sessionStorage.getItem("tempWorkspaceId");
      if (tempWsId) {
        workspaceid.current = tempWsId;
        localStorage.removeItem("tempWorkspaceId");
      }
    }
    setOpenDialog(true);
  };

  const loadDialogDefaults = () => {
    if (selectedLink.current) {
      newLink.current = {
        id: selectedLink.current.id,
        name: selectedLink.current.name,
        url: selectedLink.current.url,
      };
    } else {
      newLink.current = {
        id: "",
        name: "",
        url: "",
      };
    }
  };

  const handleConfirm = () => {
    if (newLink.current.name && newLink.current.url) {
      if (selectedLink.current) {
        updateProjectLink();
        selectedLink.current = null;
      } else {
        addProjectLink();
      }

      handleClose();
    }
  };

  const handleDotsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDotsClose = () => {
    setAnchorEl(null);
  };

  const dotsColumnTemplate = (props) => {
    return (
      <Box
        onClick={helpMode ? null : handleDotsClick}
        id={helpMode ? "__dashboardProjectLinksMenu" : props.index}
        rowindex={props.index}
        sx={{
          height: "25px",
          width: "25px",
          cursor: helpMode ? "help" : "pointer",
          color: "gray",
          borderRadius: "25px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <MoreVertIcon rowindex={props.index} />
      </Box>
    );
  };

  const removeLink = (event) => {
    handleDotsClose();
    const tempLinkArray = JSON.parse(JSON.stringify(linkArray));
    const delIndex = parseInt(event.target.dataset.linkinfo);

    const linkId = tempLinkArray[delIndex].id;

    const deleteString =
      authString +
      '<Modify type="Workspacelink"><Workspacelink><id>' +
      linkId +
      "</id><workspaceid>9999</workspaceid></Workspacelink></Modify></request>";
    Tools.fetchPost(deleteString, urlString);

    tempLinkArray.splice(delIndex, 1);

    setLinkArray(tempLinkArray);
  };

  function addProjectLink() {
    props.waitHandler(true);
    const urlParts = newLink.current.url.split("://");

    const requestString =
      authString +
      '<Add type="Workspacelink"><Workspacelink><workspaceid>' +
      workspaceid.current +
      "</workspaceid><external>1</external><description>" +
      newLink.current.name +
      "</description><url>" +
      urlParts[0] +
      "://" +
      encodeURIComponent(urlParts[1]).replace(/%3F/g, "?") +
      "</url></Workspacelink></Add></request>";

    Tools.fetchPost(requestString, urlString).then((addResultXml) => {
      const addResult = Tools.xmlToArray(
        addResultXml.xml.childNodes[0].childNodes[1].childNodes,
      );
      const tempLinkArray = JSON.parse(JSON.stringify(linkArray));
      tempLinkArray.push({
        id: addResult[0].id,
        url: newLink.current.url,
        name: newLink.current.name,
      });
      props.waitHandler(false);
      setLinkArray(tempLinkArray);
    });
  }

  function updateProjectLink() {
    const urlParts = newLink.current.url.split("://");

    const requestString =
      authString +
      '<Modify type="Workspacelink"><Workspacelink><id>' +
      newLink.current.id +
      "</id><workspaceid>" +
      workspaceid.current +
      "</workspaceid><external>1</external><description>" +
      newLink.current.name +
      "</description><url>" +
      urlParts[0] +
      "://" +
      encodeURIComponent(urlParts[1]).replace(/%3F/g, "?") +
      "</url></Workspacelink></Modify></request>";

    Tools.fetchPost(requestString, urlString);
    const tempLinkArray = JSON.parse(JSON.stringify(linkArray));
    const matchingIndex = tempLinkArray.findIndex(
      (elem) => elem.id == newLink.current.id,
    );

    tempLinkArray.splice(matchingIndex, 1, {
      id: newLink.current.id,
      url: newLink.current.url,
      name: newLink.current.name,
    });
    setLinkArray(tempLinkArray);
  }

  const handleTextFieldValueChange = (event) => {
    const fieldname = event.target.id.substring(
      event.target.id.indexOf("_", event.target.id.indexOf("__") + 2) + 1,
    );
    newLink.current[fieldname] = event.target.value;
  };

  const linkTemplate = (link) => {
    return (
      <div>
        {helpMode ? (
          link.name
        ) : (
          <a target="_blank" rel="noreferrer" href={link.url}>
            {link.name}
          </a>
        )}
      </div>
    );
  };

  const headerTemplate = () => {
    return <></>;
  };

  const createWorkspace = () => {
    props.waitHandler(true);
    setOpenDialog(false);
    const today = new Date();
    const createWorkspaceRequest =
      authString +
      '<Add type="Workspace"><Workspace><name>' +
      props.projectObject.name +
      " workspace</name><description>Created by OACC</description><open>1</open><userid>" +
      creds.id +
      "</userid><date><Date><year>" +
      today.getFullYear() +
      "</year><month>" +
      (parseInt(today.getMonth()) + 1) +
      "</month><day>" +
      today.getDate() +
      "</day></Date></date></Workspace></Add></request>";

    Tools.fetchPost(createWorkspaceRequest, urlString, "Workspace").then(
      (rawWsRec) => {
        const wsRec = Tools.xmlToArray(rawWsRec.xml);
        const wsId = wsRec[0].id;

        const createLinkRequest =
          authString +
          '<Add type="Workspacelink"><Workspacelink><workspaceid>' +
          wsId +
          "</workspaceid><recordid>" +
          props.projectObject.id +
          "</recordid><table_name>project</table_name></Workspacelink></Add></request>";
        Tools.fetchPost(createLinkRequest, urlString).then(() => {
          console.log("link created");
          workspaceid.current = wsId;
          sessionStorage.setItem("tempWorkspaceId", wsId);
          props.waitHandler(false);
          setOpenConfirmation(true);
        });
      },
    );
  };

  const handleConfirmationClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenConfirmation(false);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenDialog(false);
  };

  const addLinkComponent = (
    <AddLinkIcon
      id="__dashboardProjectLinksAdd"
      //onClick={helpMode ? null : handleOpen}
      onClick={handleOpen}
      fontSize="small"
      sx={{
        marginRight: "5px",
        //cursor: helpMode ? "help" : "pointer",
      }}
    />
  );

  const openDotsMenu = Boolean(anchorEl);

  useEffect(() => {
    const prepDisplayPage = (
      <Fragment>
        <CustomAlert
          alertText="Workspace Created"
          openAlert={openConfirmation}
          setOpenAlert={setOpenConfirmation}
        />

        <Box
          id="__dashboardProjectLinks"
          height="12%"
          className="dashTableTitleBar"
          sx={{ cursor: helpMode ? "help" : "default" }}
        >
          <DashboardPanelHeader
            panelLocked={props.panelLocked}
            title="Links"
            removePanel={props.removePanel}
            panelId={props.panelId}
            headerComponent={addLinkComponent}
          />
        </Box>

        <Box
          id="__dashboardProjectLinks"
          height="85%"
          sx={{
            overflow: "auto",
            verticalAlign: "middle",
            border: "solid 2px",
            padding: "0px 4px 4px 4px",
            borderRadius: "0px 0px 6px 6px",
            backgroundColor: "white",
            cursor: helpMode ? "help" : "default",
          }}
        >
          <GridComponent
            id="__dashboardProjectLinks_grid"
            className="gridWithNoHeader"
            enableStickyHeader={true}
            dataSource={linkArray}
            ref={(g) => (grid.current = g)}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="url"
                headerText=""
                textAlign="Left"
                template={linkTemplate}
                headerTemplate={headerTemplate}
                customAttributes={{ class: "projectLinkNameCell" }}
              />
              <ColumnDirective
                field="dots"
                width="25"
                headerText=""
                textAlign="Right"
                template={dotsColumnTemplate}
                headerTemplate={headerTemplate}
                customAttributes={{ class: "projectTeamDotsCell" }}
              />
            </ColumnsDirective>
          </GridComponent>
        </Box>

        <Dialog open={openDialog} onClose={helpMode ? null : handleClose}>
          {workspaceid.current ? (
            <Box
              sx={{
                backgroundColor: "gainsboro",
                padding: "10px",
                border: "solid 2px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "large",
                  backgroundColor: "#643f69",
                  color: "white",
                  borderRadius: "5px",
                  paddingLeft: "5px",
                  marginBottom: "15px",
                }}
              >
                Add Link
              </Typography>
              <TextField
                required
                id="__dashboardProjectLinksName_name"
                defaultValue={newLink.current.name}
                onChange={helpMode ? null : handleTextFieldValueChange}
                variant="standard"
                label="Display Name"
                sx={{ margin: "0px 10px", width: "250px" }}
              ></TextField>
              <TextField
                required
                id="__dashboardProjectLinksURL_url"
                defaultValue={newLink.current.url}
                onChange={helpMode ? null : handleTextFieldValueChange}
                variant="standard"
                label="URL"
                sx={{ margin: "0px 10px", width: "250px" }}
              ></TextField>
              <br></br>
              <br></br>
              <DialogActions sx={{ justifyContent: "center" }}>
                <Button
                  className="dialogConfirmCancelButton"
                  onClick={helpMode ? null : handleClose}
                >
                  Cancel
                </Button>
                <Button
                  className="dialogConfirmCancelButton"
                  onClick={helpMode ? null : handleConfirm}
                >
                  Confirm
                </Button>
              </DialogActions>
            </Box>
          ) : (
            <DialogContent
              sx={{
                overflow: "hidden",
                padding: "0px",
                backgroundColor: "#ed6c02",
              }}
            >
              <Alert
                variant="filled"
                onClose={handleAlertClose}
                severity="warning"
                sx={{
                  width: "95%",
                  fontSize: "large",
                  padding: "0px",
                  margin: "5px",
                }}
              >
                <Typography sx={{ padding: "25px" }}>
                  This project does not have an associated Openair workspace. In
                  order to add links a workspace must be created and linked to
                  this project. Click &quot;OK&quot; to create a workspace and
                  link it to this project.
                </Typography>
                <DialogActions sx={{ paddingRight: "30px" }}>
                  <Button
                    className="settingsButtonActive"
                    variant="filled"
                    value="OK"
                    onClick={createWorkspace}
                    sx={{ margin: "0px 10px 0px 0px !important" }}
                  >
                    OK
                  </Button>
                  <Button
                    className="settingsButtonActive"
                    variant="filled"
                    value="Cancel"
                    onClick={handleClose}
                    sx={{ margin: "0px !important" }}
                  >
                    Cancel
                  </Button>
                </DialogActions>
              </Alert>
            </DialogContent>
          )}
        </Dialog>
        <DashboardStyledMenu
          id="__dashboardProjectLinksMenu"
          open={openDotsMenu}
          anchorEl={anchorEl}
          onClose={handleDotsClose}
        >
          <MenuItem
            data-linkinfo={anchorEl ? anchorEl.id : null}
            onClick={helpMode ? null : handleOpen}
          >
            Edit Link
          </MenuItem>
          <MenuItem
            data-linkinfo={anchorEl ? anchorEl.id : null}
            onClick={helpMode ? null : removeLink}
          >
            Remove Link
          </MenuItem>
        </DashboardStyledMenu>
      </Fragment>
    );
    setDisplayPage(prepDisplayPage);
  }, [openDialog, openConfirmation, openDotsMenu, linkArray, helpMode]);

  return displayPage;
}

export default DashboardProjectLinks;

DashboardProjectLinks.propTypes = {
  waitHandler: PropTypes.func,
  projectObject: PropTypes.object,
  panelLocked: PropTypes.bool,
  removePanel: PropTypes.func,
  panelId: PropTypes.number,
  wlTable: PropTypes.array,
  index: PropTypes.number, // shoudln't be necessary
};
/*
              <AddLinkIcon
                id="__dashboardProjectLinksAdd"
                onClick={helpMode ? null : handleOpen}
                fontSize="small"
                sx={{
                  marginRight: "5px",
                  cursor: helpMode ? "help" : "pointer",
                }}
              />
              */
